import type { Cotacao } from '@/db';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(value);
}

interface KPICardsProps {
  cotacoes: Cotacao[];
  canSeeSensitive: boolean;
}

export default function KPICards({ cotacoes, canSeeSensitive }: KPICardsProps) {
  const totalCotacoes = cotacoes.length;
  const volumeTotal = cotacoes.reduce((sum, c) => sum + (c.totalBrl || c.total), 0);
  const ticketMedio = totalCotacoes > 0 ? volumeTotal / totalCotacoes : 0;
  const margemMedia = totalCotacoes > 0
    ? cotacoes.reduce((sum, c) => sum + c.margemMedia, 0) / totalCotacoes
    : 0;

  const clientesAtendidos = new Set(cotacoes.map((c) => c.clienteCodigo)).size;

  const produtoCounts: Record<string, number> = {};
  cotacoes.forEach((c) => {
    c.itens.forEach((item) => {
      produtoCounts[item.materialDescricao] = (produtoCounts[item.materialDescricao] || 0) + 1;
    });
  });
  const produtoMaisCotado = Object.entries(produtoCounts).sort((a, b) => b[1] - a[1])[0];

  const cards = [
    {
      label: 'Total de Cotações',
      value: String(totalCotacoes),
      icon: 'ri-file-list-3-line',
      color: 'bg-primary-500',
      light: 'bg-primary-100',
      text: 'text-primary-500',
    },
    {
      label: 'Volume Total',
      value: formatCurrency(volumeTotal),
      icon: 'ri-coins-line',
      color: 'bg-accent-500',
      light: 'bg-accent-100',
      text: 'text-accent-500',
    },
    {
      label: 'Ticket Médio',
      value: formatCurrency(ticketMedio),
      icon: 'ri-calculator-line',
      color: 'bg-secondary-500',
      light: 'bg-secondary-100',
      text: 'text-secondary-500',
    },
    {
      label: canSeeSensitive ? 'Margem Média' : 'Status de Margem',
      value: canSeeSensitive ? `${formatNumber(margemMedia)}%` : margemMedia >= 40 ? 'Verde' : margemMedia >= 35 ? 'Amarelo' : 'Vermelho',
      icon: 'ri-percent-line',
      color: 'bg-primary-700',
      light: 'bg-primary-100',
      text: 'text-primary-700',
    },
    {
      label: 'Clientes Atendidos',
      value: String(clientesAtendidos),
      icon: 'ri-user-follow-line',
      color: 'bg-secondary-700',
      light: 'bg-secondary-100',
      text: 'text-secondary-700',
    },
    {
      label: 'Produto Mais Cotado',
      value: produtoMaisCotado ? produtoMaisCotado[0] : '-',
      sub: produtoMaisCotado ? `${produtoMaisCotado[1]} cotações` : '',
      icon: 'ri-vip-crown-line',
      color: 'bg-accent-500',
      light: 'bg-accent-100',
      text: 'text-accent-500',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4">
      {cards.map((card) => (
        <div
          key={card.label}
          className="bg-white rounded-xl border border-background-200/70 p-4 flex items-start gap-3"
        >
          <div className={`w-10 h-10 rounded-lg ${card.light} flex items-center justify-center shrink-0`}>
            <i className={`${card.icon} ${card.text} text-lg`} />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-foreground-900 font-semibold">{card.label}</p>
            <p className="text-sm font-bold text-foreground-950 truncate mt-0.5 font-tabular">
              {card.value}
            </p>
            {card.sub && (
              <p className="text-[10px] text-foreground-800 mt-0.5">{card.sub}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
